import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { Dialog } from '@material-ui/core';

import { Spinner } from 'core/components/spinner';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { appConfig } from 'brand-config';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import { dateTimeFormatterInUserTimezone } from 'core/helpers/formatters';
import triangleAlertIcon from 'core/assets/svg/triangle-alert.svg';
import useStyles from './offer-redirect-interstitial.styles';

/**
 * Auto-redirect interstitial (#30994): opening an older offer in a lineage redirects staff to the
 * newest one, framed as "loading the most recent saved changes". The "Keep version" link pauses
 * the redirect behind a confirmation; staying lasts the current page view only — navigating back
 * re-triggers.
 */

// The lookup already finished in getOffer — the dwell is purely so users can read what's
// happening (and reach "Keep version" if they mean to). Brand-configurable because the mutual
// carriers' agents asked for longer to react; the fallback keeps a brand that declares nothing on
// the original 5s.
const DEFAULT_INTERSTITIAL_MS = 5000;

const STEPS = {
  REDIRECTING: 'REDIRECTING',
  CONFIRM: 'CONFIRM'
};

const formatDateAtTime = (dateTime) =>
  dateTime ? dateTimeFormatterInUserTimezone(dateTime, { separator: ' at ' }) : null;

const OfferRedirectInterstitial = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const {
    offer: { redirectTarget, dismissRedirect, setNavigatingInternally, offer }
  } = useStore();
  // Session isAgency (not the offer store's affinity-lookup isAgencyUser) so both events share one definition
  const { isAgency } = useSession();

  const [step, setStep] = useState(STEPS.REDIRECTING);
  const timerRef = useRef(null);

  const offerID = offer?.id;
  const webUserId = offer?.webUserId;

  const doRedirect = useCallback(() => {
    track('Staff - Offer Auto-Redirected', {
      isAgency,
      // Empty = direct/pasted URL; otherwise staff-internal vs comp-rater bridge domains
      referrer: document.referrer,
      offerID,
      webUserId
    });
    // Skip the newer-offer check once on arrival — the destination is already the newest offer
    setNavigatingInternally(true);
    // Carry only ?rater= (comp-rater bridge attribution) across the redirect. The
    // destination is a different offer, so preserving ?type= would force its displayed
    // option (offer-store reads it as raterOption) and could hide a bundle (#34438).
    const rater = new URLSearchParams(history.location.search).get('rater');
    history.push({
      pathname: `/offer/${redirectTarget?.id}`,
      search: rater ? `?${new URLSearchParams({ rater })}` : ''
    });
  }, [isAgency, offerID, webUserId, setNavigatingInternally, history, redirectTarget?.id]);

  useEffect(() => {
    if (step !== STEPS.REDIRECTING) {
      return undefined;
    }
    timerRef.current = setTimeout(doRedirect, appConfig?.offerRedirectInterstitialMs ?? DEFAULT_INTERSTITIAL_MS);
    return () => clearTimeout(timerRef.current);
  }, [step, doRedirect]);

  // Cancel the pending dwell before redirecting by hand, so the timer can't fire a second
  // history.push behind the one the click already did.
  const onLoadNowClick = () => {
    clearTimeout(timerRef.current);
    doRedirect();
  };

  const onKeepVersionClick = () => {
    clearTimeout(timerRef.current);
    setStep(STEPS.CONFIRM);
  };

  const onStay = () => {
    track("Staff - Don't Redirect to Updated Offer Clicked", { isAgency, offerID, webUserId });
    dismissRedirect();
  };

  // Both copies reference the *current* offer's last-updated time — everything after it is what
  // staying would miss; creation time covers never-updated root offers
  const currentVersionTimestamp = formatDateAtTime(offer?.updatedDateTime || offer?.initialCreationDateTime);

  if (step === STEPS.CONFIRM) {
    return (
      <Dialog open classes={{ paper: classes.confirmPaper }}>
        <div className={classes.confirmContent}>
          <img src={triangleAlertIcon} className={classes.warningIcon} alt="Warning" />
          <div className={classes.confirmCopy}>
            <Label type="greenBig">Changes will be lost</Label>
            <Label type="modalBody" className={classes.confirmBody}>
              This quote does not include any updates made since {currentVersionTimestamp || 'its last update'}. Are you
              sure you want to stay here?
            </Label>
          </div>
        </div>
        <div className={classes.confirmActions}>
          <Button onClick={onStay} variant="contained" color="secondary">
            Stay on this offer
          </Button>
          <Button onClick={doRedirect} variant="contained" color="primary">
            Load most recent version
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open classes={{ paper: classes.interstitialPaper }}>
      <div className={classes.spinner}>
        <Spinner size={80} />
      </div>
      <Label type="modalBody" className={classes.interstitialText}>
        Loading your most recent saved changes...
      </Label>
      <div className={classes.interstitialActions}>
        {/* The dwell exists so staff can read the modal and opt out — it should never be the only
            way forward for someone who already knows they want the newer offer. */}
        <button type="button" className={classes.loadNowLink} onClick={onLoadNowClick}>
          Load it now
        </button>
        <button type="button" className={classes.keepVersionLink} onClick={onKeepVersionClick}>
          {currentVersionTimestamp ? `Keep version from ${currentVersionTimestamp}` : 'Keep this version'}
        </button>
      </div>
    </Dialog>
  );
});

export default OfferRedirectInterstitial;
